import { ReactElement } from 'react';
import { CopyableText } from '@client/common/components/CopyableText';
import { DATA_TABLE_BREAKPOINT, DataTable } from '@client/common/components/DataTable';
import { Tooltip } from '@client/common/components/Tooltip';
import { useTranslate } from '@client/i18n/hooks';
import { getCdnImageUrl, getProductFallbackImgUrl } from '@client/routes/utils/url';
import {
  ACTION_LINK_ICON_NAME,
  ACTION_LINK_ICON_SIZE,
  ACTION_LINK_THEME,
  ActionLink,
} from '@lib/components/ActionLink';
import { Alert, ALERT_DISPLAY_TYPE, ALERT_STYLE_TYPE } from 'b2b-components/theme/components/Alert';
import { CardBox } from 'b2b-components/theme/components/CardBox';
import { DATA_PAIR_DIRECTION, DATA_PAIR_STYLE_TYPE, DataPair } from 'b2b-components/theme/components/DataPair';
import { RmaInvoiceType } from 'b2b-common/core/account/api/rma';
import { Props } from './SearchResults.types';
import './SearchResults.scss';

export const SearchResults = ({
  areResultsLimited,
  invoiceLink,
  onRepairClick,
  onReturnAllClick,
  onReturnClick,
  resultCount,
  results,
  rmaItemLink,
}: Props): ReactElement => {
  const t = useTranslate();
  const isInvoiceReturnable = (invoice: RmaInvoiceType): boolean => (
    !!invoice.items.filter((item) => item.isReturnPossible).length
  );
  const debitorId = results?.[0]?.debitor || '';

  return (
    <div className='RmaSearchResults'>
      <div className='RmaSearchResults-Title'>
        {`${t('Matching results')} (${resultCount})`}
      </div>

      {areResultsLimited && (
        <Alert
          displayType={ALERT_DISPLAY_TYPE.text}
          styleType={ALERT_STYLE_TYPE.info}
          className='RmaSearchResults-Alert'
        >
          {t(
            'Your search request rmas to many hits. Please choose a more specific search term',
          )}
        </Alert>
      )}

      {results.length <= 0 && (
        <Alert
          displayType={ALERT_DISPLAY_TYPE.text}
          styleType={ALERT_STYLE_TYPE.info}
          className='RmaSearchResults-Alert'
        >
          {t('Your search did not return any results.')}
        </Alert>
      )}

      {results.length > 0
        && results.map((invoice) => (
          <CardBox className='RmaSearchResults-CardBox' key={invoice.invoiceNumber}>
            <div className='RmaSearchResults-Data'>
              <DataPair
                className='RmaSearchResults-InvoiceLabel'
                direction={DATA_PAIR_DIRECTION.column}
                isColonDisplayed={false}
                label={t('Invoice number')}
                styleType={DATA_PAIR_STYLE_TYPE.small}
              >
                <a
                  data-hj-suppress
                  className='cds-ButtonLink RmaSearchResults-InvoiceLink'
                  href={invoiceLink.href({
                    invoiceId: invoice.invoiceNumber,
                  })}
                  onClick={invoiceLink.handleClick({
                    invoiceId: invoice.invoiceNumber,
                  })}
                >
                  {invoice.invoiceNumber}
                </a>
              </DataPair>

              <DataPair
                className='RmaSearchResults-InvoiceLabel'
                direction={DATA_PAIR_DIRECTION.column}
                isColonDisplayed={false}
                label={t('Invoice date')}
                styleType={DATA_PAIR_STYLE_TYPE.small}
              >
                <span className='RmaSearchResults-InvoiceDate'>
                  {invoice.date}
                </span>
              </DataPair>

              <DataPair
                className='RmaSearchResults-InvoiceLabel'
                direction={DATA_PAIR_DIRECTION.column}
                isColonDisplayed={false}
                label={t('Order reference')}
                styleType={DATA_PAIR_STYLE_TYPE.small}
              >
                <span data-hj-suppress className='RmaSearchResults-InvoiceReference'>
                  {invoice.reference}
                </span>
              </DataPair>

              <div className='RmaSearchResults-ReturnPossibilityWrapper'>
                {isInvoiceReturnable(invoice) ? (
                  <ActionLink
                    iconSize={ACTION_LINK_ICON_SIZE.small}
                    iconName={ACTION_LINK_ICON_NAME.return}
                    onClick={onReturnAllClick(invoice.invoiceNumber, debitorId)}
                    className='RmaSearchResults-ReturnPossibility'
                  >
                    {t('Return multiple items')}
                  </ActionLink>
                ) : (
                  <ActionLink
                    iconSize={ACTION_LINK_ICON_SIZE.small}
                    iconName={ACTION_LINK_ICON_NAME.return}
                    theme={ACTION_LINK_THEME.dark}
                    className='RmaSearchResults-ReturnPossibility'
                  >
                    {t('Returns are not possible for this order any longer')}
                  </ActionLink>
                )}
              </div>
            </div>

            <div className='RmaSearchResults-Table'>
              <DataTable
                className='ProductTable ProductTable--WithThumbnail RmaProductTable'
                breakpoint={DATA_TABLE_BREAKPOINT.sm}
                isHoverable
                rows={invoice.items.map((item) => ({
                  cells: [
                    {
                      className: 'ProductTable-BodyCell--Thumbnail RmaProductTable-BodyCell--Thumbnail',
                      value: (
                        <img
                          alt={item.title || t('This item cannot be displayed due to incomplete data')}
                          className='ProductTable-Thumbnail'
                          src={
                            getCdnImageUrl(item.cover) || getProductFallbackImgUrl()
                          }
                        />
                      ),
                    },
                    {
                      className: 'RmaProductTable-BodyCell--Title',
                      value: (
                        <div className='RmaSearchResults-BodyCell--Title'>
                          {item.isActive ? (
                            <a
                              className='MyAccount-ProductName'
                              href={rmaItemLink.href({
                                sku: item.sku,
                              })}
                              onClick={rmaItemLink.handleClick({
                                sku: item.sku,
                              })}
                            >
                              {item.title}
                            </a>
                          ) : (
                            <>
                              {item.title ? (
                                <Tooltip
                                  content={t('This item is no longer available for purchase')}
                                >
                                  <span className='MyAccount-ProductName--Inactive'>{item.title}</span>
                                </Tooltip>
                              ) : (
                                <Alert
                                  displayType={ALERT_DISPLAY_TYPE.text}
                                  styleType={ALERT_STYLE_TYPE.warning}
                                >
                                  <div>
                                    <span>{t('This item cannot be displayed due to incomplete data')}</span>
                                    {!!item?.sku && (
                                      <>
                                        &nbsp;
                                        <span>({t('Art.-Nr.')} {item.sku})</span>
                                      </>
                                    )}
                                  </div>
                                </Alert>
                              )}
                            </>
                          )}

                          <DataPair
                            direction={DATA_PAIR_DIRECTION.row}
                            label={t('SKU')}
                            styleType={DATA_PAIR_STYLE_TYPE.small}
                          >
                            <CopyableText>{item.sku}</CopyableText>
                          </DataPair>

                          {item.productReference && (
                            <div
                              className='ProductTable-Reference'
                              data-hj-suppress
                            >
                              {item.productReference}
                            </div>
                          )}
                        </div>
                      ),
                    },
                    {
                      className: 'RmaProductTable-BodyCell--Warranty',
                      value: (
                        <DataPair
                          className='ProductTable-Warranty'
                          label={t('Warranty until')}
                          styleType={DATA_PAIR_STYLE_TYPE.small}
                        >
                          {item.warranty.validUntil}
                        </DataPair>
                      ),
                    },
                    {
                      className: 'RmaProductTable-BodyCell--Amount cds-DataTable-BodyCell--Bolder',
                      value: item.amount,
                    },
                    {
                      className: 'RmaProductTable-BodyCell--Actions',
                      value: (
                        <div className='RmaSearchResults-TableButtons'>
                          <ActionLink
                            data-hj-suppress
                            iconSize={ACTION_LINK_ICON_SIZE.small}
                            iconName={ACTION_LINK_ICON_NAME.repair}
                            onClick={onRepairClick(
                              invoice.invoiceNumber,
                              item.sku,
                              debitorId,
                            )}
                            className='RmaSearchResults-TableButton'
                          >
                            {t('Repair')}
                          </ActionLink>

                          {item.isReturnPossible && (
                            <ActionLink
                              data-hj-suppress
                              iconSize={ACTION_LINK_ICON_SIZE.small}
                              iconName={ACTION_LINK_ICON_NAME.return}
                              onClick={onReturnClick(
                                invoice.invoiceNumber,
                                item.sku,
                                debitorId,
                              )}
                              className='RmaSearchResults-TableButton'
                            >
                              {t('Return')}
                            </ActionLink>
                          )}
                        </div>
                      ),
                    },
                  ],
                }))}
              />
            </div>
          </CardBox>
        ))}
    </div>
  );
};
